import { PopoverContentProps } from '@radix-ui/react-popover';
import { forwardRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'v5/platform/dom/cn';
import { useControllableState } from 'v5/platform/react/useControllableState';
import { Button, type ButtonProps } from 'v5/design-system/Button';
import {
  defaultColorLibrary,
  findColorLibraryItem,
  type ColorPickerLibrary
} from './ColorPicker.library';
import {
  ColorPickerGridGallery,
  usePickerGridFocusRef
} from './ColorPickerGridGallery';
import {
  ColorSwatchPreview,
  useColorPickerDismissFix
} from './ColorPicker.shared';
import {
  resolveColorDisplay,
  resolveColorLibraryItemValue
} from './ColorPicker.utils';
import { DropdownMenuButtonArrow } from 'v5/design-system/DropdownMenu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';

export type ColorPickerProps = {
  value: string;
  onChange: (value: string) => void;
  library?: ColorPickerLibrary;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  align?: PopoverContentProps['align'];
  side?: PopoverContentProps['side'];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
};

/**
 * Color select: DS {@link Button} as Radix {@link PopoverTrigger} + swatch grid.
 * Enter / Space / focus restore are left to Radix.
 */
export const ColorPicker = forwardRef<HTMLButtonElement, ColorPickerProps>(
  (
    {
      value,
      onChange,
      library = defaultColorLibrary,
      disabled,
      readOnly,
      placeholder,
      align = 'start',
      side,
      open: openProp,
      onOpenChange,
      variant = 'secondary',
      size = 'sm',
      className
    },
    ref
  ) => {
    const { t } = useTranslation('patterns', { keyPrefix: 'ColorPicker' });
    const resolvedPlaceholder = placeholder ?? t('placeholder');
    const [open, setOpen] = useControllableState({
      prop: openProp,
      onChange: onOpenChange,
      fallbackValue: false
    });
    const { ref: colorGridRef, focusGrid } = usePickerGridFocusRef();
    const labelId = useId();
    const selectedId =
      findColorLibraryItem(library, value)?.id ?? library.colors[0]?.id ?? '';
    const display = resolveColorDisplay(library, value);
    const isInteractive = !disabled && !readOnly;
    const { rootProps, popoverProps } = useColorPickerDismissFix(open, setOpen);

    return (
      <Popover
        open={isInteractive && open}
        onOpenChange={next => {
          if (!isInteractive) return;
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            ref={ref}
            type="button"
            variant={variant}
            size={size}
            disabled={disabled}
            aria-readonly={readOnly || undefined}
            tabIndex={readOnly ? -1 : undefined}
            aria-labelledby={labelId}
            className={cn(
              'w-full justify-between font-normal',
              readOnly && 'pointer-events-none cursor-default',
              className
            )}
            {...rootProps}
            onKeyDown={e => {
              if (isInteractive && !open && e.key === 'ArrowDown') {
                e.preventDefault();
                setOpen(true);
              }
            }}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <ColorSwatchPreview
                hex={display.hex}
                render={display.swatchRender}
              />
              <span
                id={labelId}
                className={cn(
                  'min-w-0 truncate text-left',
                  display.name ? 'text-fg-default' : 'text-fg-muted'
                )}
              >
                {display.name ?? resolvedPlaceholder}
              </span>
            </span>
            {isInteractive ? (
              <DropdownMenuButtonArrow className="shrink-0 opacity-50" />
            ) : null}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align={align}
          side={side}
          className="w-80 p-3"
          {...popoverProps}
          onOpenAutoFocus={e => {
            e.preventDefault();
            focusGrid();
          }}
        >
          <ColorPickerGridGallery
            gridRef={colorGridRef}
            ariaLabel={resolvedPlaceholder}
            colors={library.colors}
            selectedId={selectedId}
            onSelectedIdChange={nextId => {
              const item = library.colors.find(color => color.id === nextId);
              if (item) {
                onChange(resolveColorLibraryItemValue(item));
                setOpen(false);
              }
            }}
            onClose={() => setOpen(false)}
          />
        </PopoverContent>
      </Popover>
    );
  }
);

ColorPicker.displayName = 'ColorPicker';

export default ColorPicker;
